import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import {
  AgeChip,
  BackLink,
  CardAside,
  CardHead,
  CardLayout,
  CardMain,
  CardWrapper,
  ClientName,
  Collapsible,
  CollapsibleCount,
  CollapsibleHeader,
  Fact,
  FactDetail,
  FactLabel,
  FactValue,
  HeadMeta,
  LeadGrid,
  Muted,
  QuestionBlock,
  RatingChip,
  RatingCompanion,
  RowLabel,
  RowList,
  RowValue,
  Section,
  SectionTitle,
  ServiceHeadline,
  ServiceServed,
  ServiceStrip,
  SummaryBlock,
} from "../IntakeReviewStyled";
import {
  closeIntakeReviewCard,
  noteSectionOpened,
} from "../../../slice/IntakeReviewSlice";
import { ageLabel, leadByKey, ratingChipText, ratingTone } from "../utils";
import DecisionBar from "./DecisionBar";
import PiReviewCard from "./PiReviewCard";

/**
 * One collapsible group of corroborating detail.
 *
 * Expanding it is recorded once per card. That is the evidence for which
 * facts actually carry the decision, which is how the lead-fact ordering
 * gets corrected by usage instead of by argument.
 */
const CardSection = ({ section }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) dispatch(noteSectionOpened(section.key));
  };

  return (
    <Collapsible>
      <CollapsibleHeader type="button" onClick={toggle} aria-expanded={open}>
        <span>
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}{" "}
          {section.title}
        </span>
        <CollapsibleCount>
          {section.row_count}{" "}
          {section.row_count === 1 ? "answer" : "answers"}
        </CollapsibleCount>
      </CollapsibleHeader>
      {open ? (
        <RowList>
          {(section.rows || []).map((row, index) => (
            <div key={`${section.key}-${index}`} style={{ display: "contents" }}>
              <RowLabel>{row.label}</RowLabel>
              <RowValue>{row.value}</RowValue>
            </div>
          ))}
        </RowList>
      ) : null}
    </Collapsible>
  );
};

/**
 * The reviewing attorney's card.
 *
 * The twelve facts twelve months of decisions say carry the call sit up
 * front, unconditionally — including when they are empty, because "no
 * rating recorded" is itself evidence and a card that hides the gap
 * teaches nobody anything. Everything else is grouped and collapsed, and
 * an empty group is not rendered at all: Litify shows ~300 fields in
 * fixed order whether or not they were answered, and the wall is the
 * thing being replaced.
 */
const ReviewCard = () => {
  const dispatch = useDispatch();
  const { card, cardLoading, cardError } = useSelector(
    (state) => state.intakeReviews
  );

  if (cardLoading) return <Muted>Opening the intake…</Muted>;
  if (cardError) return <Muted role="alert">{cardError}</Muted>;
  if (!card) return null;

  // Each practice area reads differently. The card says which it is;
  // the VA layout below is the default for anything that does not.
  if (card.card === "pi" || card.snapshot?.card === "pi") {
    return <PiReviewCard />;
  }

  const snapshot = card.snapshot || {};
  const identity = snapshot.identity || {};
  const source = snapshot.source || {};
  const lead = Array.isArray(snapshot.lead) ? snapshot.lead : [];
  const sections = Array.isArray(snapshot.sections) ? snapshot.sections : [];
  const priorQuestion = snapshot.prior_question || {};
  const service = snapshot.service || null;
  const rating = leadByKey(snapshot).combined_rating?.rating;

  return (
    <CardWrapper>
      <BackLink type="button" onClick={() => dispatch(closeIntakeReviewCard())}>
        ← Back to the queue
      </BackLink>

      <CardLayout>
        <CardMain>
          <CardHead>
            <ClientName>{card.clientName || "Unnamed intake"}</ClientName>
            <HeadMeta>
              <RatingChip $tone={ratingTone(rating?.band)}>
                {ratingChipText(rating)}
              </RatingChip>
              {/* The veteran's age, not the queue wait — both belong
                  up here and they are easy to confuse, so the wait is
                  worded as a wait. */}
              {identity.age ? (
                <AgeChip
                  $tone="neutral"
                  title={
                    identity.age.of_client
                      ? "Age of the claimant, not the veteran"
                      : identity.age.date_of_birth || ""
                  }
                >
                  {identity.age.label}
                  {identity.age.of_client ? " (claimant)" : ""}
                </AgeChip>
              ) : null}
              {typeof card.ageDays === "number" ? (
                <AgeChip $tone={card.ageTone} title={card.waitingSince || ""}>
                  Waiting {ageLabel(card.ageDays)}
                </AgeChip>
              ) : null}
              {identity.case_type ? <span>{identity.case_type}</span> : null}
              {card.intakeNumber ? <span>· {card.intakeNumber}</span> : null}
              {source.name ? <span>· via {source.name}</span> : null}
            </HeadMeta>
          </CardHead>

          {service ? (
            <ServiceStrip>
              <ServiceHeadline>{service.headline}</ServiceHeadline>
              {service.served_label ? (
                <ServiceServed>
                  <strong>Intake recorded: </strong>
                  {service.served_label}
                </ServiceServed>
              ) : null}
              {service.deployments ? (
                <ServiceServed>
                  <strong>Deployments: </strong>
                  {service.deployments}
                </ServiceServed>
              ) : null}
              {service.duties ? (
                <ServiceServed>
                  <strong>Duties: </strong>
                  {service.duties}
                </ServiceServed>
              ) : null}
            </ServiceStrip>
          ) : null}

          {priorQuestion.question ? (
            <Section>
              <QuestionBlock>
                <strong>This went back to intake with a question</strong>
                {priorQuestion.question}
                {priorQuestion.answer ? (
                  <>
                    <strong style={{ marginTop: 8 }}>Intake answered</strong>
                    {priorQuestion.answer}
                  </>
                ) : (
                  <em> — no answer yet.</em>
                )}
              </QuestionBlock>
            </Section>
          ) : null}

          <Section>
            <SectionTitle>What decides this case</SectionTitle>
            <LeadGrid>
              {lead.map((fact) => (
                <Fact key={fact.key}>
                  <FactLabel>{fact.label}</FactLabel>
                  <FactValue $absent={!fact.present}>
                    {fact.present ? fact.value : "Not recorded"}
                  </FactValue>
                  {/* Only ever a grant: a denial or a lapsed award
                      stays in the work section, never in this box. */}
                  {fact.tdiu?.said ? (
                    <RatingCompanion>
                      <strong>TDIU</strong>
                      Intake: “{fact.tdiu.said}”
                    </RatingCompanion>
                  ) : null}
                  {/* A discharge that is not honorable is not one
                      thing: general under honorable usually keeps VA
                      eligibility, other than honorable can bar it. */}
                  {fact.detail ? (
                    <FactDetail>{fact.detail}</FactDetail>
                  ) : null}
                </Fact>
              ))}
            </LeadGrid>
          </Section>

          {sections.length ? (
            <Section>
              <SectionTitle>Everything else on the intake</SectionTitle>
              {sections.map((section) => (
                <CardSection key={section.key} section={section} />
              ))}
            </Section>
          ) : null}

          {snapshot.intake_summary ? (
            <Section>
              <SectionTitle>Intake's own summary</SectionTitle>
              <SummaryBlock>{snapshot.intake_summary}</SummaryBlock>
            </Section>
          ) : null}
        </CardMain>

        <CardAside>
          <DecisionBar />
        </CardAside>
      </CardLayout>
    </CardWrapper>
  );
};

export default ReviewCard;
